import { claimPuzzleAchievement, getReachedPuzzleMilestone, getReachedPuzzleMedal } from "./puzzleAchievements";
import Head from "next/head";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSettings } from "@/redux/settings";
import { useUser } from "@/redux/user";
import { api } from "@/services/api";
import { Button } from "@/components/ui/button";
import { LEAGUE_NAME_PLACEMENT } from "@chesshotel/constants";
import PuzzleBoard, { PuzzleRatingUpdate } from "./PuzzleBoard";
import {
  createEmptyPuzzleSolvedProgress,
  getPuzzleLeagueMedalImageSrc,
  getPuzzleLeagueTier,
  getPuzzleSolvedProgressStorageKey,
  PUZZLE_PLACEMENT_TARGET,
  PuzzleLeagueTier,
  readPuzzleSolvedProgress,
  recordPuzzleSolved,
} from "./puzzleMedals";
import {
  getPuzzleDifficultyOption,
  PuzzleDifficultyKey,
  readPuzzleDifficulty,
  writePuzzleDifficulty,
} from "./puzzleDifficulty";
import useTranslation from "next-translate/useTranslation";

const MAX_PLAYED_PUZZLES = 300;
const STARTING_PUZZLE_RATING = 800;
const PUZZLE_PLACEMENT_SOLVES = PUZZLE_PLACEMENT_TARGET;
const PUZZLE_PLACEMENT_K_FACTOR = 48;
const PUZZLE_STANDARD_K_FACTOR = 24;
const GUEST_PUZZLE_RATING_STORAGE_KEY = "puzzle_rating_guest_v2";
const GUEST_PUZZLE_ATTEMPTS_STORAGE_KEY = "puzzle_rating_attempts_guest_v2";
const GUEST_PUZZLE_POINTS_STORAGE_KEY = "puzzle_points_guest_v1";
const GUEST_PUZZLE_LEAGUE_STORAGE_KEY = "puzzle_league_guest_v1";
const GUEST_PUZZLE_PROMOTION_STORAGE_KEY = "puzzle_promotion_guest_v1";

export type PuzzlePayload = {
  season?: number | null;
  puzzleId?: string;
  fen: string;
  moves: string;
  rating?: number;
  userElo?: number;
  gameNr?: number;
  leagueName?: string;
  divisionId?: number;
  solvedCount?: number;
  points?: number;
  cleanSolveStreak?: number;
  promotionProgress?: number;
  rank?: number | null;
  nextRank?: number | null;
  nextRankPoints?: number | null;
};

function getPlayedPuzzleStorageKey(username?: string) {
  return username ? `played_puzzles_${username}` : "played_puzzles_guest";
}

function readPlayedPuzzleIds(storageKey: string) {
  try {
    const storedPlayed = localStorage.getItem(storageKey);
    const parsed = storedPlayed ? JSON.parse(storedPlayed) : [];
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch (e) {
    console.error("Error reading played puzzles", e);
    return [];
  }
}

function rememberPlayedPuzzle(storageKey: string, puzzleId: string) {
  try {
    const played = readPlayedPuzzleIds(storageKey);
    if (!played.includes(puzzleId)) {
      played.push(puzzleId);
    }
    const recentPlayed = played.slice(-MAX_PLAYED_PUZZLES);
    localStorage.setItem(storageKey, JSON.stringify(recentPlayed));
    return recentPlayed;
  } catch (e) {
    console.error("Error saving played puzzle", e);
    return [];
  }
}

function normalizePuzzleRating(value: unknown) {
  const rating = Number(value);
  return Number.isFinite(rating) && rating > 0
    ? Math.round(rating)
    : STARTING_PUZZLE_RATING;
}

function normalizePuzzleAttempts(value: unknown) {
  const attempts = Number(value);
  return Number.isFinite(attempts) && attempts >= 0
    ? Math.floor(attempts)
    : 0;
}

function readGuestPuzzleRating() {
  if (typeof window === "undefined") return STARTING_PUZZLE_RATING;

  try {
    return normalizePuzzleRating(
      window.localStorage.getItem(GUEST_PUZZLE_RATING_STORAGE_KEY),
    );
  } catch (e) {
    console.error("Error reading puzzle rating", e);
    return STARTING_PUZZLE_RATING;
  }
}

function writeGuestPuzzleRating(rating: number) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      GUEST_PUZZLE_RATING_STORAGE_KEY,
      String(normalizePuzzleRating(rating)),
    );
  } catch (e) {
    console.error("Error saving puzzle rating", e);
  }
}

function readGuestPuzzleAttempts() {
  if (typeof window === "undefined") return 0;

  try {
    return normalizePuzzleAttempts(
      window.localStorage.getItem(GUEST_PUZZLE_ATTEMPTS_STORAGE_KEY),
    );
  } catch (e) {
    console.error("Error reading puzzle attempts", e);
    return 0;
  }
}

function writeGuestPuzzleAttempts(attempts: number) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      GUEST_PUZZLE_ATTEMPTS_STORAGE_KEY,
      String(normalizePuzzleAttempts(attempts)),
    );
  } catch (e) {
    console.error("Error saving puzzle attempts", e);
  }
}

function readGuestPuzzlePoints() {
  if (typeof window === "undefined") return 0;

  try {
    return normalizePuzzleAttempts(
      window.localStorage.getItem(GUEST_PUZZLE_POINTS_STORAGE_KEY),
    );
  } catch (e) {
    console.error("Error reading puzzle points", e);
    return 0;
  }
}

function writeGuestPuzzlePoints(points: number) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      GUEST_PUZZLE_POINTS_STORAGE_KEY,
      String(normalizePuzzleAttempts(points)),
    );
  } catch (e) {
    console.error("Error saving puzzle points", e);
  }
}

function readGuestPuzzleLeague() {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage.getItem(GUEST_PUZZLE_LEAGUE_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
}

function writeGuestPuzzleLeague(leagueName?: string) {
  if (typeof window === "undefined") return;
  try {
    if (leagueName) {
      window.localStorage.setItem(GUEST_PUZZLE_LEAGUE_STORAGE_KEY, leagueName);
    } else {
      window.localStorage.removeItem(GUEST_PUZZLE_LEAGUE_STORAGE_KEY);
    }
  } catch {
    // The current session still works when browser storage is unavailable.
  }
}

function readGuestPromotionProgress() {
  if (typeof window === "undefined") return 0;
  try {
    return normalizePuzzleAttempts(
      window.localStorage.getItem(GUEST_PUZZLE_PROMOTION_STORAGE_KEY),
    );
  } catch {
    return 0;
  }
}

function writeGuestPromotionProgress(progress: number) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      GUEST_PUZZLE_PROMOTION_STORAGE_KEY,
      String(normalizePuzzleAttempts(progress)),
    );
  } catch {
    // The current session still works when browser storage is unavailable.
  }
}

function isPuzzleRatingRevealed({
  attempts,
}: {
  attempts: number;
}) {
  return attempts >= PUZZLE_PLACEMENT_SOLVES;
}

function PuzzleChallengeMessage({
  title,
  text,
}: {
  title: string;
  text: string;
}) {
  const settings = useSettings();
  const boardSize = settings.boardSize || 504;
  const panelHeight = settings.isDesktop ? boardSize + 44 + "px" : undefined;

  return (
    <div className="flex flex-col items-center md:mt-4">
      <div className="flex w-full flex-col items-center md:w-auto md:flex-row md:items-start md:rounded-lg">
        <div className="flex justify-center md:mr-4">
          <div
            className="flex items-center justify-center rounded-md border border-athens-gray-150 bg-white text-sm text-gray-500"
            style={{ width: boardSize, height: boardSize }}
          >
            {title}
          </div>
        </div>
        <div
          className="mb-20 flex h-full w-full flex-col justify-center rounded-md bg-athens-gray-150 px-5 text-sm md:mb-0 md:w-[400px] lg:w-[400px]"
          style={{ height: panelHeight }}
        >
          <div className="text-lg font-bold text-gray-800">{title}</div>
          <div className="mt-2 text-muted-foreground">{text}</div>
        </div>
      </div>
    </div>
  );
}

function PuzzleLeagueUnlockedPopup({
  tier,
  milestone,
  guest,
  onClose,
}: {
  tier: PuzzleLeagueTier;
  milestone?: number | null;
  guest: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation("puzzles");
  const leagueLabel = t(`league.tiers.${tier.label.toLowerCase()}`);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="puzzle-promotion-title" className="fixed inset-0 z-[1000] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-gray-950/45" onClick={onClose} />
      <div className="relative flex w-full max-w-[420px] flex-col items-center rounded-lg bg-white px-6 pb-6 pt-7 text-center shadow-2xl">
        <button
          className="absolute right-4 top-3 text-2xl leading-none text-gray-400 hover:text-gray-700"
          onClick={onClose}
          aria-label={t("league.closeCelebration")}
          type="button"
        >
          ×
        </button>
        <img
          src={getPuzzleLeagueMedalImageSrc(tier)}
          alt={t("league.viewStandingsLabel", { league: leagueLabel })}
          className="h-24 w-24 object-contain drop-shadow-md"
        />
        <div id="puzzle-promotion-title" className="mt-4 text-xl font-bold text-gray-900">
          {t("league.welcomeLeague", { league: leagueLabel })}
        </div>
        <div className="mt-2 text-sm text-muted-foreground">
          {t("league.enteredLeague", { league: leagueLabel })}
        </div>
        {milestone != null && <div className="mt-4 rounded-md bg-slate-100 px-4 py-3 text-sm font-semibold text-altblue-700">{t("league.alsoPointsReached", { points: milestone.toLocaleString() })}</div>}
        {guest ? (
          <>
            <p className="mt-4 text-sm text-slate-600">
              {t("league.createAccountLong")}
            </p>
            <Button asChild autoFocus className="mt-5 w-full" size="lg">
              <Link href="/register">{t("mistakes.createAccount")}</Link>
            </Button>
            <Button className="mt-2 w-full" variant="quiet" onClick={onClose}>
              {t("league.continueSolving")}
            </Button>
          </>
        ) : (
          <Button autoFocus className="mt-6 w-full" size="lg" onClick={onClose}>
            {t("league.continueSolving")}
          </Button>
        )}
      </div>
    </div>
  );
}

type PuzzleChallengeProps = {
  initialPuzzle?: PuzzlePayload | null;
};

export default function PuzzleChallenge({
  initialPuzzle = null,
}: PuzzleChallengeProps) {
  const { t } = useTranslation("puzzles");
  const user = useUser();
  const username = user?.memberLoggedIn ? user.username : undefined;
  const [currentPuzzle, setCurrentPuzzle] =
    useState<PuzzlePayload | null>(initialPuzzle);
  const [prefetchedPuzzle, setPrefetchedPuzzle] =
    useState<PuzzlePayload | null>(null);
  const [nextLoading, setNextLoading] = useState(false);
  const [preferencesReady, setPreferencesReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [solvedProgress, setSolvedProgress] = useState(
    createEmptyPuzzleSolvedProgress,
  );
  const [cleanSolveStreak, setCleanSolveStreak] = useState(0);
  const [promotionProgress, setPromotionProgress] = useState(0);
  const [unlockedLeague, setUnlockedLeague] =
    useState<PuzzleLeagueTier | null>(null);
  const [medalReached, setMedalReached] = useState<number | null>(null);
  const [season, setSeason] = useState<number | null>(initialPuzzle?.season ?? null);
  const [milestoneReached, setMilestoneReached] = useState<number | null>(null);
  const [promotionMilestone, setPromotionMilestone] = useState<number | null>(null);
  const [puzzleDifficulty, setPuzzleDifficulty] =
    useState<PuzzleDifficultyKey>("balanced");
  const [puzzleRating, setPuzzleRating] = useState(
    normalizePuzzleRating(initialPuzzle?.userElo),
  );
  const [ratingGameNr, setRatingGameNr] = useState(
    normalizePuzzleAttempts(initialPuzzle?.gameNr),
  );
  const [ratingLeagueName, setRatingLeagueName] = useState<string | undefined>(
    initialPuzzle?.leagueName,
  );
  const [puzzlePoints, setPuzzlePoints] = useState(
    normalizePuzzleAttempts(initialPuzzle?.points),
  );
  const [lastPointsEarned, setLastPointsEarned] = useState<number | null>(null);
  const [lastStreakBonus, setLastStreakBonus] = useState(0);
  const [rank, setRank] = useState<number | null>(initialPuzzle?.rank ?? null);
  const [nextRank, setNextRank] = useState<number | null | undefined>(
    initialPuzzle?.nextRank ?? null,
  );
  const [nextRankPoints, setNextRankPoints] = useState<
    number | null | undefined
  >(
    initialPuzzle?.nextRankPoints ?? null,
  );
  const [guestPuzzleAttempts, setGuestPuzzleAttempts] = useState(0);
  const prefetchingRef = useRef(false);
  const currentPuzzleIdRef = useRef<string | undefined>(
    initialPuzzle?.puzzleId,
  );
  const puzzleDifficultyRef = useRef<PuzzleDifficultyKey>("balanced");
  const puzzleRatingRef = useRef(STARTING_PUZZLE_RATING);
  const usernameRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    currentPuzzleIdRef.current = currentPuzzle?.puzzleId;
  }, [currentPuzzle?.puzzleId]);

  useEffect(() => {
    puzzleDifficultyRef.current = puzzleDifficulty;
  }, [puzzleDifficulty]);

  useEffect(() => {
    puzzleRatingRef.current = puzzleRating;
  }, [puzzleRating]);

  const applyFetchedPuzzleRating = useCallback((puzzle: PuzzlePayload) => {
    if (puzzle.season !== undefined) setSeason(puzzle.season);
    if (puzzle.userElo !== undefined) {
      setPuzzleRating(normalizePuzzleRating(puzzle.userElo));
    }
    if (puzzle.gameNr !== undefined) {
      setRatingGameNr(normalizePuzzleAttempts(puzzle.gameNr));
    }
    if (puzzle.leagueName !== undefined) {
      setRatingLeagueName(puzzle.leagueName);
    }
    if (puzzle.solvedCount !== undefined) {
      const solvedCount = normalizePuzzleAttempts(puzzle.solvedCount);
      setSolvedProgress((currentProgress) => ({
        ...currentProgress,
        solved: Math.max(currentProgress.solved, solvedCount),
      }));
    }
    if (puzzle.points !== undefined) {
      setPuzzlePoints(normalizePuzzleAttempts(puzzle.points));
    }
    if (puzzle.cleanSolveStreak !== undefined) {
      setCleanSolveStreak(normalizePuzzleAttempts(puzzle.cleanSolveStreak));
    }
    if (puzzle.promotionProgress !== undefined) {
      setPromotionProgress(normalizePuzzleAttempts(puzzle.promotionProgress));
    }
    if (puzzle.rank !== undefined) {
      setRank(puzzle.rank);
    }
    if (puzzle.nextRank !== undefined) {
      setNextRank(puzzle.nextRank);
    }
    if (puzzle.nextRankPoints !== undefined) {
      setNextRankPoints(puzzle.nextRankPoints);
    }
  }, []);

  const requestPuzzle = useCallback(
    async (extraPlayedPuzzleIds: string[] = []) => {
      const placement =
        ratingGameNr < PUZZLE_PLACEMENT_SOLVES ||
        ratingLeagueName === LEAGUE_NAME_PLACEMENT;
      const difficultyOption = getPuzzleDifficultyOption(
        placement ? "balanced" : puzzleDifficulty,
      );
      const storageKey = getPlayedPuzzleStorageKey(username);
      const playedPuzzleIds = Array.from(
        new Set([
          ...readPlayedPuzzleIds(storageKey),
          ...extraPlayedPuzzleIds.filter(Boolean),
        ]),
      ).slice(-MAX_PLAYED_PUZZLES);

      const response = await api.post("/newpuzzle/random", {
        elo: puzzleRating,
        range: difficultyOption.range,
        difficultyOffset: difficultyOption.ratingOffset,
        playedPuzzleIds,
      });

      return response.data as PuzzlePayload;
    },
    [puzzleDifficulty, puzzleRating, ratingGameNr, ratingLeagueName, username],
  );

  const prefetchPuzzle = useCallback(
    async (extraPlayedPuzzleIds: string[] = []) => {
      if (prefetchingRef.current) return;

      const requestedDifficulty = puzzleDifficulty;
      const requestedRating = puzzleRating;
      prefetchingRef.current = true;
      try {
        const puzzle = await requestPuzzle(extraPlayedPuzzleIds);
        if (
          puzzleDifficultyRef.current === requestedDifficulty &&
          puzzleRatingRef.current === requestedRating
        ) {
          setPrefetchedPuzzle(puzzle);
        }
      } catch (e) {
        console.error("Error prefetching puzzle", e);
      } finally {
        prefetchingRef.current = false;
      }
    },
    [puzzleDifficulty, puzzleRating, requestPuzzle],
  );

  useEffect(() => {
    const previousUsername = usernameRef.current;
    usernameRef.current = username;
    if (previousUsername !== username) setCurrentPuzzle(null);
    setPrefetchedPuzzle(null);
    setSolvedProgress(
      readPuzzleSolvedProgress(getPuzzleSolvedProgressStorageKey(username)),
    );

    if (username) {
      if (previousUsername !== username) {
        setPuzzleRating(STARTING_PUZZLE_RATING);
        setRatingGameNr(0);
        setRatingLeagueName(LEAGUE_NAME_PLACEMENT);
        setPuzzlePoints(0);
        setLastPointsEarned(null);
        setLastStreakBonus(0);
        setCleanSolveStreak(0);
        setPromotionProgress(0);
        setRank(null);
        setNextRank(null);
        setNextRankPoints(null);
      }

      return;
    }

    const storedGuestRating = readGuestPuzzleRating();
    const storedGuestAttempts = readGuestPuzzleAttempts();
    const storedGuestPoints = readGuestPuzzlePoints();
    const storedGuestLeague = readGuestPuzzleLeague();
    const storedPromotionProgress = readGuestPromotionProgress();
    setPuzzleRating(storedGuestRating);
    setGuestPuzzleAttempts(storedGuestAttempts);
    setRatingGameNr(storedGuestAttempts);
    setRatingLeagueName(
      storedGuestAttempts >= PUZZLE_PLACEMENT_SOLVES
        ? storedGuestLeague || getPuzzleLeagueTier({ rating: storedGuestRating }).leagueName
        : undefined,
    );
    setPuzzlePoints(storedGuestPoints);
    setPromotionProgress(storedPromotionProgress);
    setRank(null);
    setNextRank(undefined);
    setNextRankPoints(undefined);
  }, [username]);

  useEffect(() => {
    setPuzzleDifficulty(readPuzzleDifficulty());
    setPreferencesReady(true);
  }, []);

  useEffect(() => {
    setPrefetchedPuzzle(null);
    if (currentPuzzle?.puzzleId) {
      void prefetchPuzzle([currentPuzzle.puzzleId]);
    }
  }, [currentPuzzle?.puzzleId, prefetchPuzzle, puzzleDifficulty]);

  useEffect(() => {
    let cancelled = false;

    async function preparePuzzle() {
      if (!preferencesReady) return;
      if (currentPuzzle) {
        if (!prefetchedPuzzle && currentPuzzle.puzzleId) {
          void prefetchPuzzle([currentPuzzle.puzzleId]);
        }
        return;
      }

      try {
        setLoadError(false);
        const puzzle = await requestPuzzle();
        if (cancelled) return;
        if (username) applyFetchedPuzzleRating(puzzle);
        setCurrentPuzzle(puzzle);
        if (puzzle.puzzleId) {
          void prefetchPuzzle([puzzle.puzzleId]);
        }
      } catch (e) {
        if (cancelled) return;
        console.error("Error fetching puzzle", e);
        setLoadError(true);
        setCurrentPuzzle(null);
      }
    }

    void preparePuzzle();

    return () => {
      cancelled = true;
    };
  }, [currentPuzzle, prefetchedPuzzle, prefetchPuzzle, requestPuzzle, preferencesReady, retryCount, username, applyFetchedPuzzleRating]);

  const handleNext = async () => {
    if (nextLoading) return;
    setMilestoneReached(null);
    setMedalReached(null);

    const currentPuzzleId = currentPuzzle?.puzzleId;

    if (prefetchedPuzzle) {
      const nextPuzzle = prefetchedPuzzle;
      if (username) {
        applyFetchedPuzzleRating(nextPuzzle);
      }
      setCurrentPuzzle(nextPuzzle);
      setPrefetchedPuzzle(null);
      if (nextPuzzle.puzzleId) {
        void prefetchPuzzle([nextPuzzle.puzzleId]);
      }
      return;
    }

    setNextLoading(true);
    try {
      const puzzle = await requestPuzzle(
        currentPuzzleId ? [currentPuzzleId] : [],
      );
      applyFetchedPuzzleRating(puzzle);
      setCurrentPuzzle(puzzle);
      if (puzzle.puzzleId) {
        void prefetchPuzzle([puzzle.puzzleId]);
      }
    } catch (e) {
      console.error("Error fetching puzzle", e);
    } finally {
      setNextLoading(false);
    }
  };

  const handleComplete = () => {
    if (!currentPuzzle?.puzzleId) return;

    const storageKey = getPlayedPuzzleStorageKey(username);
    rememberPlayedPuzzle(storageKey, currentPuzzle.puzzleId);
    const nextSolvedProgress = recordPuzzleSolved(
      getPuzzleSolvedProgressStorageKey(username),
      currentPuzzle.puzzleId,
    );
    setSolvedProgress(nextSolvedProgress);

    if (!prefetchedPuzzle) {
      void prefetchPuzzle([currentPuzzle.puzzleId]);
    }
  };

  const handleResult = (won: boolean, cleanSolve: boolean) => {
    if (!won || !cleanSolve) {
      setCleanSolveStreak(0);
      setLastStreakBonus(0);
    }
  };

  const handleDifficultyChange = (difficulty: PuzzleDifficultyKey) => {
    setPuzzleDifficulty(difficulty);
    writePuzzleDifficulty(difficulty);
    setPrefetchedPuzzle(null);
  };

  const ratingRevealed = isPuzzleRatingRevealed({
    attempts: ratingGameNr,
  });

  const handleRatingChange = (ratingUpdate: PuzzleRatingUpdate) => {
    const nextRating = normalizePuzzleRating(ratingUpdate.newElo);
    if (ratingUpdate.season !== undefined) setSeason(ratingUpdate.season);
    let nextGameNr =
      ratingUpdate.gameNr !== undefined
        ? normalizePuzzleAttempts(ratingUpdate.gameNr)
        : ratingGameNr;
    let nextLeagueName = ratingUpdate.leagueName ?? ratingLeagueName;
    const puzzleAlreadySolved = currentPuzzle?.puzzleId
      ? solvedProgress.completedPuzzleIds.includes(currentPuzzle.puzzleId)
      : false;
    const predictedSolvedCount = ratingUpdate.won && !puzzleAlreadySolved
      ? solvedProgress.solved + 1
      : solvedProgress.solved;
    const nextSolvedCount =
      ratingUpdate.solvedCount !== undefined
        ? Math.max(
            predictedSolvedCount,
            normalizePuzzleAttempts(ratingUpdate.solvedCount),
          )
        : predictedSolvedCount;

    setPuzzleRating(nextRating);
    setPrefetchedPuzzle(null);

    if (!username) {
      nextGameNr = guestPuzzleAttempts + 1;
      let nextPromotionProgress = 0;
      if (nextGameNr === PUZZLE_PLACEMENT_SOLVES) {
        nextLeagueName = getPuzzleLeagueTier({ rating: nextRating }).leagueName;
      } else if (nextGameNr > PUZZLE_PLACEMENT_SOLVES) {
        const currentTier = getPuzzleLeagueTier({
          rating: puzzleRating,
          leagueName: ratingLeagueName,
        });
        const earnedTier = getPuzzleLeagueTier({ rating: nextRating });
        if (earnedTier.minRating > currentTier.minRating) {
          nextPromotionProgress = ratingUpdate.won
            ? promotionProgress + 1
            : 0;
          if (nextPromotionProgress >= 3) {
            nextLeagueName = earnedTier.leagueName;
            nextPromotionProgress = 0;
          }
        }
      }
      writeGuestPuzzleRating(nextRating);
      writeGuestPuzzleAttempts(nextGameNr);
      writeGuestPuzzleLeague(nextLeagueName);
      writeGuestPromotionProgress(nextPromotionProgress);
      setGuestPuzzleAttempts(nextGameNr);
      setPromotionProgress(nextPromotionProgress);
    }

    if (ratingUpdate.points !== undefined) {
      const nextPoints = normalizePuzzleAttempts(ratingUpdate.points);
      setPuzzlePoints(nextPoints);
      if (!username) {
        writeGuestPuzzlePoints(nextPoints);
      }
    }
    if (ratingUpdate.pointsEarned !== undefined) {
      setLastPointsEarned(normalizePuzzleAttempts(ratingUpdate.pointsEarned));
    }
    if (ratingUpdate.streakBonus !== undefined) {
      setLastStreakBonus(normalizePuzzleAttempts(ratingUpdate.streakBonus));
    }
    if (ratingUpdate.cleanSolveStreak !== undefined) {
      setCleanSolveStreak(
        normalizePuzzleAttempts(ratingUpdate.cleanSolveStreak),
      );
    }
    if (ratingUpdate.promotionProgress !== undefined) {
      setPromotionProgress(
        normalizePuzzleAttempts(ratingUpdate.promotionProgress),
      );
    }
    if (ratingUpdate.rank !== undefined) {
      setRank(ratingUpdate.rank);
    }
    if (ratingUpdate.nextRank !== undefined) {
      setNextRank(ratingUpdate.nextRank);
    }
    if (ratingUpdate.nextRankPoints !== undefined) {
      setNextRankPoints(ratingUpdate.nextRankPoints);
    }

    setRatingGameNr(nextGameNr);
    setRatingLeagueName(nextLeagueName);
    if (ratingUpdate.solvedCount !== undefined) {
      setSolvedProgress((currentProgress) => ({
        ...currentProgress,
        solved: Math.max(currentProgress.solved, nextSolvedCount),
      }));
    }

    const nextRatingRevealed = isPuzzleRatingRevealed({
      attempts: nextGameNr,
    });

    const previousTier = getPuzzleLeagueTier({ rating: puzzleRating, leagueName: ratingLeagueName });
    const nextTier = getPuzzleLeagueTier({ rating: nextRating, leagueName: nextLeagueName });
    const owner = username || "guest";
    const reached = getReachedPuzzleMilestone(puzzlePoints, ratingUpdate.points ?? puzzlePoints);
    const milestone = nextRatingRevealed && reached !== null &&
      claimPuzzleAchievement(owner, `points:${reached}`) ? reached : null;
    const promoted = nextRatingRevealed && (!ratingRevealed || nextTier.minRating > previousTier.minRating) &&
      claimPuzzleAchievement(owner, `league:${nextTier.leagueName}`);

    const achievementSeason = ratingUpdate.season ?? season;
    const reachedMedal = getReachedPuzzleMedal(rank, ratingUpdate.rank);
    const newMedal = ratingRevealed && nextRatingRevealed && achievementSeason !== null &&
      (season === null || season === achievementSeason) && previousTier.leagueName === nextTier.leagueName &&
      reachedMedal !== null && claimPuzzleAchievement(owner, `season:${achievementSeason}:league:${nextTier.leagueName}:medal:${reachedMedal}`)
      ? reachedMedal : null;

    if (promoted) {
      setMedalReached(null);
      setMilestoneReached(null);
      setPromotionMilestone(milestone);
      setUnlockedLeague(nextTier);
    } else {
      setMedalReached(newMedal);
      if (milestone !== null) setMilestoneReached(milestone);
    }
  };

  const guestRatingKFactor =
    ratingGameNr < PUZZLE_PLACEMENT_SOLVES
      ? PUZZLE_PLACEMENT_K_FACTOR
      : PUZZLE_STANDARD_K_FACTOR;

  return (
    <>
      <Head>
        <title>{t("league.metaTitle")}</title>
        <meta
          name="description"
          content={t("league.metaDescription")}
        />
      </Head>
      <main className="min-h-[calc(100vh-80px)] px-0 pb-6 md:px-4 md:pb-20">
        {currentPuzzle ? (
          <PuzzleBoard
            key={currentPuzzle.puzzleId || currentPuzzle.fen}
            medalReached={medalReached}
            milestoneReached={milestoneReached}
            fen={currentPuzzle.fen}
            moves={currentPuzzle.moves}
            onNext={handleNext}
            onComplete={handleComplete}
            onResult={handleResult}
            onRatingChange={handleRatingChange}
            puzzleRating={currentPuzzle.rating}
            userElo={puzzleRating}
            gameNr={ratingGameNr}
            leagueName={ratingLeagueName}
            solvedProgress={solvedProgress}
            puzzlePoints={puzzlePoints}
            lastPointsEarned={lastPointsEarned}
            lastStreakBonus={lastStreakBonus}
            rank={rank}
            nextRank={nextRank}
            nextRankPoints={nextRankPoints}
            cleanSolveStreak={cleanSolveStreak}
            promotionProgress={promotionProgress}
            nextLoading={nextLoading}
            puzzleDifficulty={puzzleDifficulty}
            ratingKFactor={guestRatingKFactor}
            ratingRevealed={ratingRevealed}
            onPuzzleDifficultyChange={handleDifficultyChange}
          />
        ) : (
          <div className="px-4 py-12 text-center" role="status">
            <p>{t(loadError ? "league.loadFailed" : "league.loading")}</p>
            {loadError && <Button className="mt-4" onClick={() => setRetryCount((count) => count + 1)}>{t("general.tryAgain")}</Button>}
          </div>
        )}
      </main>
      {unlockedLeague && (
        <PuzzleLeagueUnlockedPopup
          tier={unlockedLeague}
          milestone={promotionMilestone}
          guest={!user?.memberLoggedIn}
          onClose={() => setUnlockedLeague(null)}
        />
      )}
    </>
  );
}
